export interface RateLimiterSettings {
  maxCapacity: number;
  refillRate: number;
  refillRateIntervalMs: number;
}

export interface ConcurrentRequestsLimitation {
  maxConcurrentRequests: number;
}
export interface RateLimitationSettings
  extends Partial<RateLimiterSettings>,
    Partial<ConcurrentRequestsLimitation> {}

export type RateLimiters<TRateLimiterId extends string> = {
  [id in TRateLimiterId]: Partial<RateLimiterSettings>;
};

export interface RateLimitationSettingsWithMultipleRateLimiters<
  TRateLimiterId extends string,
> extends Partial<ConcurrentRequestsLimitation> {
  rateLimiters?: Partial<RateLimiters<TRateLimiterId>>;
}

export interface BaseVendorApiClientSettings
  extends Partial<VendorApiExistingSettings> {
  respectSystemProxyConfiguration: boolean;
  /**
   * Every vendor API client collects and logs its statistics. The `clientStatsIntervalSec` sets the client statistics interval in seconds.
   */
  clientStatsIntervalSec?: number;
  slug?: string;
}

interface BaseVendorApiWithoutRateLimitation<Type>
  extends BaseVendorApiClientSettings {
  type: Type;
  isEnabled: boolean;
  comment?: string;
}

export interface BaseVendorApi<Type>
  extends BaseVendorApiWithoutRateLimitation<Type>,
    RateLimitationSettings {}

export type BaseUpdateVendorApiSettings<
  TPublicVendorApiSettings,
  TVendorApiAuthentication,
> = TPublicVendorApiSettings &
  // biome-ignore lint/complexity/noBannedTypes: Type necessary to allow for partial updates
  (TVendorApiAuthentication | {}) &
  VendorApiExistingSettings;

export type RateLimiterId<TRateLimiterId extends string = never> =
  | TRateLimiterId
  | 'default';

export interface BaseVendorApiWithMultipleRateLimiters<
  Type,
  TRateLimiterId extends string,
> extends BaseVendorApiWithoutRateLimitation<Type>,
    RateLimitationSettingsWithMultipleRateLimiters<
      RateLimiterId<TRateLimiterId>
    > {}

export interface RejectUnauthorized {
  rejectUnauthorized: boolean;
}

export interface CombinedDiscovery {
  combinedDiscovery?: boolean;
}

export interface ApiVersion<TValue = string> {
  apiVersion?: TValue;
}

export interface BaseUrl {
  baseUrl: string;
}

export interface BaseUrls {
  baseUrls: string[];
}

export interface PaginationSettings {
  /**
   * Default page size count
   */
  defaultPageSize?: number;
}

/**
 * Existing Vendor API Settings with id
 */
export interface VendorApiExistingSettings {
  id: string;
}
